/**
 * subcontractors — the standing facts about each subcontractor that the
 * workbook knows and the WIP ledger never will.
 *
 * SOURCE: SUBCONTRACTORS_DATA.xlsx (2026-08, Mikko), sheets
 * `SUBCONTRACTORS CLUSTERING`, `REF - Active Sewers` and
 * `TENURE (from roster)`, themselves reconciled against the two contractor
 * masterlists (BIR-registered and non-BIR).
 *
 * WHAT LIVES HERE AND WHAT DOES NOT — the line matters, because getting it
 * wrong is how a scorecard starts lying:
 *
 *   HERE      facts with no other source in the app. Where a subcontractor
 *             is (cluster + town), how many people sew under them, whether
 *             they are BIR-registered, and the day they started. None of
 *             these can be measured off BQC3/BQC4 scans, so they are
 *             carried as data.
 *   NOT HERE  anything the ledger already measures — SKU competency,
 *             capacity, turnaround, defects. The workbook has columns for
 *             all four, and they are *snapshots of this app's own output*
 *             taken in August 2026. Freezing them here would pin the
 *             scorecard to that week forever; `subconGrading.ts` recomputes
 *             them from live data instead. The workbook's own figures are
 *             kept only in the test fixture, as the thing the recomputation
 *             has to reproduce.
 *
 * KEYED BY LEDGER KEY, not by name. The WIP grid's AssignedSewer column
 * carries a surname ("VEQUISO"), the masterlist carries a full name
 * ("VEQUISO, LERMA PERALTA"), and two pairs collide: CONCEPCION is both
 * Joselito Ahon (BIR, Cluster 4) and Anamie C. (non-BIR, Cluster 1), so the
 * grid disambiguates them as CONCEPCION J. / CONCEPCION A., and NATIVIDAD
 * carries a J. suffix for the same reason. The ledger key is whatever the
 * grid actually writes — matching on anything else silently merges two
 * people's scorecards.
 *
 * TENURE IS COMPUTED, NEVER STORED. The workbook froze "8.245 years" for
 * VEQUISO on the day it was built; a stored number like that is wrong the
 * next morning and nobody notices for a year. Only `dateStarted` is data.
 */

export type BirStatus = "BIR" | "NON-BIR";

export interface ISubcontractorProfile {
  /** Matches the WIP grid's AssignedSewer, upper-case. The join key. */
  ledgerKey: string;
  /** Masterlist name, "SURNAME, GIVEN NAMES". */
  name: string;
  /**
   * Delivery cluster 1-5, or null for a subcontractor not yet placed.
   * CANDELARIA is the only one — a June 2026 start with no town on record,
   * left unassigned on purpose rather than guessed into a cluster.
   */
  cluster: number | null;
  /** Town/city. Null where the masterlist has none. */
  location: string | null;
  /** Sewers working under this subcontractor, including themselves. */
  manpower: number | null;
  bir: BirStatus;
  /** ISO date (YYYY-MM-DD) the subcontractor started. Null if unknown. */
  dateStarted: string | null;
}

/**
 * The five delivery clusters, as the workbook groups them. A cluster is a
 * pickup/drop run, not a performance band: everything in Cluster 1 is one
 * van route through Parañaque and the towns south of it. Grouping the board
 * this way is what lets a planner ask "who else is on Thursday's Cavite
 * run?" — a question tier and grade cannot answer.
 */
export const CLUSTERS: readonly { id: number; label: string; area: string }[] = [
  { id: 1, label: "Cluster 1", area: "Parañaque · Las Piñas · Bacoor" },
  { id: 2, label: "Cluster 2", area: "Dasmariñas · Imus" },
  { id: 3, label: "Cluster 3", area: "Naic · Gen. Trias · Tanza · Trece" },
  { id: 4, label: "Cluster 4", area: "Laguna · Quezon · GMA" },
  { id: 5, label: "Cluster 5", area: "Metro Manila · Rizal" },
];

export const UNCLUSTERED_LABEL = "Unassigned — confirm cluster";

/** The 57 active subcontractors, in cluster order. */
export const SUBCONTRACTORS: readonly ISubcontractorProfile[] = [
  { ledgerKey: "VEQUISO", name: "VEQUISO, LERMA PERALTA", cluster: 1, location: "PARAÑAQUE", manpower: 9, bir: "BIR", dateStarted: "2018-05-18" },
  { ledgerKey: "SANGLAY", name: "SANGLAY, JULIETA ARCILLA", cluster: 1, location: "PARAÑAQUE", manpower: 4, bir: "BIR", dateStarted: "2021-03-11" },
  { ledgerKey: "LUMBOCAN", name: "LUMBOCAN , LUCY ABOY", cluster: 1, location: "PARAÑAQUE", manpower: 2, bir: "NON-BIR", dateStarted: "2017-08-18" },
  { ledgerKey: "ALIP", name: "ALIP, MELINDA ASEJO", cluster: 1, location: "PARAÑAQUE", manpower: 1, bir: "BIR", dateStarted: "2022-04-01" },
  { ledgerKey: "LOYOLA", name: "LOYOLA, NELIA VASQUEZ", cluster: 1, location: "BACOOR", manpower: 1, bir: "BIR", dateStarted: "2024-11-12" },
  { ledgerKey: "BAGDOC", name: "BAGDOC, LIEZEL OCENA", cluster: 1, location: "LAS PIÑAS", manpower: 1, bir: "BIR", dateStarted: "2024-01-16" },
  { ledgerKey: "CONCEPCION A.", name: "CONCEPCION, ANAMIE C.", cluster: 1, location: "BACOOR", manpower: 1, bir: "NON-BIR", dateStarted: "2019-02-17" },
  { ledgerKey: "BARBA", name: "BARBA, LORIFIE", cluster: 1, location: "LAS PIÑAS", manpower: 1, bir: "NON-BIR", dateStarted: "2024-02-18" },
  { ledgerKey: "ATIS", name: "ATIS, GENITA OSUYOS", cluster: 1, location: "PARAÑAQUE", manpower: 1, bir: "BIR", dateStarted: "2023-08-01" },
  { ledgerKey: "SANICO", name: "SANICO, RONALYN ALLAGO", cluster: 1, location: "PARAÑAQUE", manpower: 1, bir: "BIR", dateStarted: "2023-08-01" },
  { ledgerKey: "TERANIA", name: "TERANIA, THELMA", cluster: 1, location: "BACOOR", manpower: 1, bir: "NON-BIR", dateStarted: "2025-04-29" },
  { ledgerKey: "REYES", name: "REYES, ELENITA RENONO", cluster: 2, location: "DASMA", manpower: 8, bir: "BIR", dateStarted: "2021-10-01" },
  { ledgerKey: "BRIOSO", name: "BAMBI, MARIA CHRISTINA BRIOSO", cluster: 2, location: "DASMA", manpower: 2, bir: "BIR", dateStarted: "2019-12-09" },
  { ledgerKey: "BAMBI", name: "BAMBI, RAY REYNALDO", cluster: 2, location: "DASMA", manpower: 1, bir: "BIR", dateStarted: "2024-10-21" },
  { ledgerKey: "BURABO", name: "BURABO, MYRNA BRAZOS", cluster: 2, location: "DASMA", manpower: 2, bir: "BIR", dateStarted: "2023-06-07" },
  { ledgerKey: "PASANA", name: "PASANA, APHRODITE BORINAGA", cluster: 2, location: "DASMA", manpower: 2, bir: "BIR", dateStarted: "2023-08-06" },
  { ledgerKey: "MAÑANITA", name: "MAÑANITA, EFREN", cluster: 2, location: "IMUS", manpower: 2, bir: "NON-BIR", dateStarted: "2022-07-20" },
  { ledgerKey: "DE VERA", name: "DE VERA, ROSARIO MANANITA", cluster: 2, location: "IMUS", manpower: 1, bir: "BIR", dateStarted: "2023-08-01" },
  { ledgerKey: "PAULE", name: "PAULE, DEMIA", cluster: 2, location: "DASMA", manpower: 2, bir: "NON-BIR", dateStarted: "2024-01-14" },
  { ledgerKey: "PADERON", name: "PADERON, BERNADITH PASCUA", cluster: 2, location: "IMUS", manpower: 1, bir: "BIR", dateStarted: "2023-10-01" },
  { ledgerKey: "CERBO", name: "CERBO, TERESITA CONCEPCION", cluster: 2, location: "IMUS", manpower: 1, bir: "BIR", dateStarted: "2024-01-16" },
  { ledgerKey: "ABOGA", name: "ABOGA, GERALYN LETRERO", cluster: 3, location: "NAIC", manpower: 5, bir: "BIR", dateStarted: "2021-01-08" },
  { ledgerKey: "RUSTIA", name: "RUSTIA, ANDRIAN GARIN", cluster: 3, location: "NAIC", manpower: 2, bir: "BIR", dateStarted: "2021-03-01" },
  { ledgerKey: "DONAYON", name: "DONAYON, JANELLE ESCULTURA", cluster: 3, location: "NAIC", manpower: 3, bir: "BIR", dateStarted: "2024-05-02" },
  { ledgerKey: "VILLANUEVA", name: "VILLANUEVA, SHERYL PEPITO", cluster: 3, location: "GENTRI", manpower: 1, bir: "BIR", dateStarted: "2024-05-01" },
  { ledgerKey: "HERMENO", name: "HERMENO, SHALEE RAMOS", cluster: 3, location: "TANZA", manpower: 4, bir: "BIR", dateStarted: "2023-09-19" },
  { ledgerKey: "MARZAN", name: "MARZAN, ROSALINA FELIPE", cluster: 3, location: "GENTRI", manpower: 1, bir: "BIR", dateStarted: "2024-02-21" },
  { ledgerKey: "LAGSAC", name: "LAGSAC, MARICEL BENOSA", cluster: 3, location: "GENTRI", manpower: 1, bir: "BIR", dateStarted: "2024-01-26" },
  { ledgerKey: "LAMATA", name: "LAMATA, MA. SHIELA", cluster: 3, location: "NAIC", manpower: 1, bir: "BIR", dateStarted: "2023-08-18" },
  { ledgerKey: "NATIVIDAD J.", name: "NATIVIDAD, JOSEPHINE L.", cluster: 3, location: "TRECE", manpower: 1, bir: "NON-BIR", dateStarted: "2023-01-01" },
  { ledgerKey: "ABAD", name: "ABAD, EVELYN", cluster: 3, location: "NAIC", manpower: 1, bir: "NON-BIR", dateStarted: "2024-06-11" },
  { ledgerKey: "RACHO", name: "RACHO, CRISTINA ALBERCA", cluster: 3, location: "GENTRI", manpower: 1, bir: "BIR", dateStarted: "2024-01-26" },
  { ledgerKey: "DELA CRUZ", name: "DELA CRUZ, CARMENCITA TARRAY", cluster: 3, location: "GENTRI", manpower: 1, bir: "BIR", dateStarted: "2023-10-01" },
  { ledgerKey: "HERRERA", name: "HERRERA, JOANA", cluster: 3, location: "NAIC", manpower: 1, bir: "NON-BIR", dateStarted: "2023-09-01" },
  { ledgerKey: "ARINGO", name: "ARINGO, LYZIEL DACUBA", cluster: 4, location: "LAGUNA", manpower: 5, bir: "BIR", dateStarted: "2023-02-23" },
  { ledgerKey: "BRUAL", name: "BRUAL, HARLEN PANGANIBAN", cluster: 4, location: "LAGUNA", manpower: 2, bir: "BIR", dateStarted: "2023-04-16" },
  { ledgerKey: "OLILA", name: "OLILA, MARIA LUISA CABATAY", cluster: 4, location: "LAGUNA", manpower: 4, bir: "BIR", dateStarted: "2020-01-06" },
  { ledgerKey: "CONCEPCION J.", name: "CONCEPCION, JOSELITO AHON", cluster: 4, location: "QUEZON", manpower: 3, bir: "BIR", dateStarted: "2024-07-24" },
  { ledgerKey: "GUEVARRA", name: "GUEVARRA, ALONA MERANA", cluster: 4, location: "LAGUNA", manpower: 1, bir: "BIR", dateStarted: "2023-09-01" },
  { ledgerKey: "ESCORIDO", name: "ESCORIDO, MARINA MERANA", cluster: 4, location: "LAGUNA", manpower: 1, bir: "BIR", dateStarted: "2023-08-26" },
  { ledgerKey: "MACASINAG", name: "MACASINAG, SHERIL", cluster: 4, location: "LAGUNA", manpower: 1, bir: "NON-BIR", dateStarted: "2023-07-10" },
  { ledgerKey: "SIENA", name: "SIENA, CRISELDA SOL", cluster: 4, location: "LAGUNA", manpower: 2, bir: "BIR", dateStarted: "2024-07-04" },
  { ledgerKey: "PACA-ANAS", name: "PACA-ANAS, ALITA MERAÑA", cluster: 4, location: "GMA", manpower: 1, bir: "BIR", dateStarted: "2023-08-26" },
  { ledgerKey: "SUAREZ", name: "SUAREZ, PERLA E.", cluster: 4, location: "QUEZON", manpower: 3, bir: "NON-BIR", dateStarted: "2023-07-01" },
  { ledgerKey: "REAL", name: "REAL , JOSEPH", cluster: 4, location: "GMA", manpower: 1, bir: "NON-BIR", dateStarted: "2025-03-31" },
  { ledgerKey: "LORENA", name: "LORENA, ROSALIE", cluster: 4, location: "LAGUNA", manpower: 1, bir: "NON-BIR", dateStarted: "2023-10-07" },
  { ledgerKey: "REGONDOLA", name: "REGONDOLA, REZIEL MANGALLENO", cluster: 5, location: "TAGUIG", manpower: 3, bir: "BIR", dateStarted: "2023-01-01" },
  { ledgerKey: "SOLEDAD", name: "SOLEDAD, VICTORIA DE VILLA", cluster: 5, location: "TAGUIG", manpower: 4, bir: "BIR", dateStarted: "2019-07-05" },
  { ledgerKey: "SALONGA", name: "SALONGA, MERCY LARGO", cluster: 5, location: "MAKATI", manpower: 3, bir: "BIR", dateStarted: "2023-01-03" },
  { ledgerKey: "AGUILAR", name: "AGUILAR, JAIME DELOS REYES", cluster: 5, location: "PASAY", manpower: 1, bir: "BIR", dateStarted: "2018-06-18" },
  { ledgerKey: "TUAZON", name: "TUAZON, DELIA NOVEDA", cluster: 5, location: "VALENZUELA", manpower: 2, bir: "BIR", dateStarted: "2024-01-11" },
  { ledgerKey: "IGDALINO", name: "IGDALINO, CRISTINA ROSALES", cluster: 5, location: "RIZAL", manpower: 1, bir: "NON-BIR", dateStarted: "2018-06-13" },
  { ledgerKey: "DE LUNA", name: "DE LUNA, JONATHAN", cluster: 5, location: "TAGUIG", manpower: 1, bir: "NON-BIR", dateStarted: "2019-05-01" },
  { ledgerKey: "MABUTIN", name: "MABUTIN, RODERICK DELA CRUZ", cluster: 5, location: "CALOOCAN", manpower: 1, bir: "BIR", dateStarted: "2023-01-23" },
  { ledgerKey: "BALLEBER", name: "BALLEBER, RECHELDA", cluster: 5, location: "MANILA", manpower: 1, bir: "NON-BIR", dateStarted: "2024-07-06" },
  { ledgerKey: "RECONES", name: "RECONES, GILDA", cluster: 5, location: "RIZAL", manpower: 1, bir: "NON-BIR", dateStarted: "2024-06-06" },
  { ledgerKey: "CANDELARIA", name: "CANDELARIA, JULIETA", cluster: null, location: null, manpower: null, bir: "NON-BIR", dateStarted: "2026-06-01" },];

const BY_KEY: ReadonlyMap<string, ISubcontractorProfile> = new Map(
  SUBCONTRACTORS.map(s => [s.ledgerKey, s])
);

/** Canonical form of a ledger key — the grid upper-cases and trims. */
export function canonKey(name: string | null | undefined): string {
  return (name ?? "").toUpperCase().trim();
}

/** The profile for a name off the WIP grid, or undefined if we hold none. */
export function profileOf(sewer: string | null | undefined): ISubcontractorProfile | undefined {
  return BY_KEY.get(canonKey(sewer));
}

/** Every profile in a cluster, in workbook order. `null` = the unassigned. */
export function clusterMembers(cluster: number | null): ISubcontractorProfile[] {
  return SUBCONTRACTORS.filter(s => s.cluster === cluster);
}

export function clusterLabel(cluster: number | null): string {
  if (cluster === null) return UNCLUSTERED_LABEL;
  const c = CLUSTERS.find(x => x.id === cluster);
  return c ? `${c.label} — ${c.area}` : `Cluster ${cluster}`;
}

const YEAR_MS = 365.25 * 86_400_000;

/**
 * Years since `dateStarted`, as a decimal — the TENURED criterion's input.
 *
 * 365.25 days a year, matching the workbook's own divisor, so a score
 * computed here lands on the same figure the sheet shows. `now` is
 * injectable because a tenure test that depends on the clock is a test that
 * starts failing on its own.
 */
export function tenureYears(dateStarted: string | null | undefined, now: Date = new Date()): number | null {
  if (!dateStarted) return null;
  const t = new Date(dateStarted).getTime();
  if (!Number.isFinite(t)) return null;
  const years = (now.getTime() - t) / YEAR_MS;
  return years > 0 ? years : 0;
}

/** Tenure for a name off the grid, from the directory's start date. */
export function tenureYearsOf(sewer: string, now: Date = new Date()): number | null {
  return tenureYears(profileOf(sewer)?.dateStarted, now);
}

export interface IDirectoryTotals {
  count: number;
  manpower: number;
  bir: number;
  nonBir: number;
}

/** Head-count rollup for a set of profiles — the pool summary's figures. */
export function totals(profiles: readonly ISubcontractorProfile[]): IDirectoryTotals {
  let manpower = 0, bir = 0, nonBir = 0;
  for (const p of profiles) {
    manpower += p.manpower ?? 0;
    if (p.bir === "BIR") bir++; else nonBir++;
  }
  return { count: profiles.length, manpower, bir, nonBir };
}
